import { Button, Form, FormGroup } from "react-bootstrap";
import PasswordTooltip from "../../../components/PasswordTooltip/PasswordTooltip";
import FormPasswordEye from "../../../components/FormPasswordEye/FormPasswordEye";
import RegistrationAcceptPolicy from "./RegistrationAcceptPolicy";
import { useLangContext } from "../../../providers/LangProvider/LangProviderContext";
import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getAppAskPhone,
    getAppFeatures,
    getIpCountry,
    getPasswordMinLengthSelector,
    makeSelectAppFormError
} from "../../../app/appSelectors";
import { useInitialUrlSearchParams } from "../../../hooks/useInitialUrlSearchParams";
import { REGISTRATION_URL_INITIAL_PARAMS } from "../../../pages/Registration/RegistrationPage";
import { useFormFieldErrors } from "../../../hooks/useFormFieldErrors";
import { REGISTRATION_FIELDS_ERRORS } from "./registrationConstants";
import { usePasswordTooltip } from "../../../components/PasswordTooltip/passwordTooltipHooks";
import { logAction, logAppsflyerAction, resetFormErrorAction } from "../../../app/appActions";
import appHistory from "../../../app/appHistory";
import { FEATURES, LOG_AF_EVENT_NAMES, RouterPaths } from "../../../app/appConstants";
import useEffectOnce from "../../../hooks/useEffectOnce";
import { useAbTestOption } from "../../AbTests/abTestsHooks";
import {
    AB_TEST_REGISTER_FIELD_PHONE_OPTIONS,
    AB_TEST_REGISTRATION_ADDITIONAL_CHECKBOX_OPTIONS,
    AB_TESTS_NAMES
} from "../../AbTests/abTestsConstants";
import LoginGoogle from "../../../pages/Login/LoginGoogle";
import { getPrevLocationSelector } from '../../Location/locationSelectors';
import useFirstStartIntroRedirect from '../../Intro/useFirstStartIntroRedirect';
import { useTelegramContext } from "../../../providers/TelegramProvider/TelegramContext";
import { Redirect } from "react-router-dom";
import { MobileRouterPaths } from "@/mobileAppCore/mobileAppConstants";

function Registration(props) {
    const { registrationPrepare } = props;

    const { getLangPath } = useLangContext();
    const formError = useSelector(makeSelectAppFormError());
    const features = useSelector(getAppFeatures);
    const passwordMinLength = useSelector(getPasswordMinLengthSelector);
    const prevLocation = useSelector(getPrevLocationSelector);
    const registerFieldPhoneOption = useAbTestOption(AB_TESTS_NAMES.REGISTER_FIELD_PHONE);
    const registrationAdditionalCheckbox = useAbTestOption(AB_TESTS_NAMES.REGISTRATION_ADDITIONAL_CHECKBOX);
    const [acceptedRules, setAcceptedRules] = useState(false);
    const formRef = useRef();
    const ipCountry = useSelector(getIpCountry);
    const { isTgAuth, initData: telegramInitData } = useTelegramContext();

    const dispatch = useDispatch();
    const [selectedIso, setSelectedIso] = useState(null);
    const askPhone = useSelector(getAppAskPhone)
    const {
        email: initialEmail,
        phone: initialPhone
    } = useInitialUrlSearchParams(REGISTRATION_URL_INITIAL_PARAMS);

    const {
        acceptPolicy: acceptPolicyError,
        email: emailError,
        password: passwordError,
        common: commonError
    } = useFormFieldErrors(REGISTRATION_FIELDS_ERRORS, formError);

    const {
        isShowPasswordTooltip, checkPasswordValue, onBlurPasswordField, onSelectPasswordField, onChangePasswordField,
    } = usePasswordTooltip();

    useFirstStartIntroRedirect();

    const handleChange = useCallback((isValid) => {
        if (!isValid) {
            dispatch(resetFormErrorAction());
        }
    }, [dispatch]);

    const handlePasswordChange = useCallback((e) => {
        handleChange(!passwordError)
        onChangePasswordField(e);
    }, [handleChange, onChangePasswordField, passwordError]);

    const submitHandler = useCallback((e) => {
        e.preventDefault();
        dispatch(logAppsflyerAction({ name: LOG_AF_EVENT_NAMES.SIGN_UP_STARTED }));
        registrationPrepare({
            acceptRules: e.target.querySelector('[name=accept-rules]').checked,
            email: e.target.querySelector('[name=email]').value,
            phone: e.target.querySelector('[name=phone]')?.value,
            country: selectedIso?.toLowerCase() || null,
            password: e.target.querySelector('[name=password]')?.value,
            telegramInitData: isTgAuth ? telegramInitData : null,
        });
    }, [dispatch, isTgAuth, registrationPrepare, selectedIso, telegramInitData]);

    useEffectOnce(() => {
        dispatch(logAction({
            page: 'sign_up',
        }));
        dispatch(logAppsflyerAction({
            name: LOG_AF_EVENT_NAMES.SIGN_UP_SHOWN,
            payload: { source: prevLocation.pathname || 'None' },
        }));
    });

    const handleLoginClick = useCallback(() => {
        dispatch(logAppsflyerAction({ name: LOG_AF_EVENT_NAMES.SIGN_UP_ALREADY_HAVE_ACCOUNT }));
        const email = document.querySelector('[name=email]').value;
        appHistory.push(`${RouterPaths.login}?email=${encodeURIComponent(email)}`);
    }, [dispatch]);

    const isShowPhoneField = useMemo(() => {
        if (ipCountry !== 'br') {
            return false;
        }
        const isEnabledByFeature = features[FEATURES.REGISTER_FIELD_PHONE];
        const isAbTestActive = registerFieldPhoneOption !== undefined;
        if (isEnabledByFeature && (!isAbTestActive || registerFieldPhoneOption === AB_TEST_REGISTER_FIELD_PHONE_OPTIONS.SHOW)) {
            return true;
        }
        return !!askPhone;
    }, [askPhone, features, ipCountry, registerFieldPhoneOption]);

    const isShowAdditionalCheckbox = useMemo(() => {
        return registrationAdditionalCheckbox === AB_TEST_REGISTRATION_ADDITIONAL_CHECKBOX_OPTIONS.ENABLED;
    }, [registrationAdditionalCheckbox]);

    const changeAcceptPolicyHandler = useCallback((e) => {
        e.stopPropagation();
        const acceptRules = formRef.current.querySelector('[name=accept-rules]')?.checked;
        const acceptRulesMultiacc = formRef.current.querySelector('[name=accept-rules-multiacc]')?.checked;
        const acceptRulesLegalYears = formRef.current.querySelector('[name=accept-rules-legal-years]')?.checked;
        handleChange(!acceptPolicyError);
        const checked = acceptRules && (acceptRulesMultiacc === undefined || acceptRulesMultiacc) && (acceptRulesLegalYears === undefined || acceptRulesLegalYears);
        setAcceptedRules(checked);
    }, [acceptPolicyError, handleChange]);

    if (isTgAuth) {
        return <Redirect to={MobileRouterPaths.login}/>;
    }

    return <>
        <Form.Group className="mb-sm-5 mb-4">
            <h1 className="text-center">{getLangPath('sign_up.title')}</h1>
        </Form.Group>
        <Form onSubmit={submitHandler} ref={formRef}>
            <div className={'d-flex flex-column gap-4'}>
                <Form.Group>
                    <Form.Control defaultValue={initialEmail} name="email" type="email"
                                  placeholder={getLangPath('sign_up.enter_email')}
                                  isInvalid={!!emailError} onChange={() => handleChange(!emailError)}
                    />
                    {
                        !!emailError && <>
                            <Form.Control.Feedback type="invalid">
                                {getLangPath(emailError)}
                                {emailError === 'error.already_registered' && (
                                    <>.&nbsp;<u onClick={handleLoginClick}>{getLangPath('promo.fx.log_in')}</u></>
                                )}
                            </Form.Control.Feedback>
                        </>
                    }
                </Form.Group>

                {
                    !!features.register_without_verify && (
                        <PasswordTooltip password={checkPasswordValue} show={isShowPasswordTooltip}>
                            <Form.Group>
                                <FormPasswordEye
                                    name="password"
                                    placeholder={getLangPath('sign_up.create_password')}
                                    isInvalid={!!passwordError}
                                    onSelect={onSelectPasswordField}
                                    onBlur={onBlurPasswordField}
                                    onChange={handlePasswordChange}
                                />
                                {
                                    !!passwordError && (
                                        <Form.Control.Feedback type="invalid">
                                            {getLangPath(passwordError, { count: passwordMinLength.toString() })}
                                        </Form.Control.Feedback>
                                    )
                                }
                            </Form.Group>
                        </PasswordTooltip>
                    )
                }

                {
                    isShowPhoneField && <Form.Group>
                        <Form.Control defaultValue={initialPhone || ''} name="phone" type="text"
                                      placeholder={getLangPath('sign_up.enter_phone_number')}/>
                    </Form.Group>
                }
            </div>

            {
                !!commonError && (
                    <Form.Control.Feedback type="invalid" className="d-block mt-3">
                        {getLangPath(commonError)}
                    </Form.Control.Feedback>
                )
            }

            <RegistrationAcceptPolicy className="mb-4 mt-4" error={acceptPolicyError}
                                      isShowAdditionalCheckbox={isShowAdditionalCheckbox}
                                      onChange={changeAcceptPolicyHandler}/>

            <div className="d-grid">
                <Button variant={'primary'} type="submit" disabled={!acceptedRules}>
                    {getLangPath('sign_up.title')}
                </Button>

                {
                    !isTgAuth && features[FEATURES.LOGIN_WITH_GOOGLE] && <>
                        <FormGroup className={'text-center mt-3'}>
                            <div className={'mb-3 text-secondary'}>{getLangPath('log_in.or')}</div>
                            <LoginGoogle disabled={!acceptedRules} isSignUp/>
                        </FormGroup>
                    </>
                }
                <Button variant={'tertiary'} onClick={handleLoginClick} className={'mt-3'}>
                    {getLangPath('sign_up.have_account')}
                </Button>
            </div>
        </Form>
    </>
}

export default Registration;
